import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ApplicationCard = ({ application, onWithdraw, onViewDetails }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const statusConfig = {
    submitted: {
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      icon: 'Send',
      label: 'Submitted'
    },
    'under-review': {
      color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      icon: 'Eye',
      label: 'Under Review'
    },
    shortlisted: {
      color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      icon: 'Star',
      label: 'Shortlisted'
    },
    'interview-scheduled': {
      color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
      icon: 'Calendar',
      label: 'Interview Scheduled'
    },
    rejected: {
      color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      icon: 'XCircle',
      label: 'Rejected'
    },
    'offer-received': {
      color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      icon: 'CheckCircle',
      label: 'Offer Received'
    }
  };

  const currentStatus = statusConfig?.[application?.status] || statusConfig?.submitted;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getDaysAgo = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays === 0 ? 'Today' : diffDays === 1 ? 'Yesterday' : `${diffDays} days ago`;
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-smooth">
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="flex-shrink-0">
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
              <Image
                src={application?.companyLogo}
                alt={application?.companyLogoAlt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-foreground mb-1 truncate">
                  {application?.jobTitle}
                </h3>
                <p className="text-sm text-muted-foreground mb-2">{application?.companyName}</p>
              </div>
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${currentStatus?.color} whitespace-nowrap`}>
                <Icon name={currentStatus?.icon} size={14} />
                <span>{currentStatus?.label}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4">
              <div className="flex items-center gap-1">
                <Icon name="MapPin" size={16} />
                <span>{application?.location}</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="Briefcase" size={16} />
                <span>{application?.experience}</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="IndianRupee" size={16} />
                <span>{application?.salary}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm mb-4">
              <div className="flex items-center gap-2">
                <Icon name="Calendar" size={16} className="text-muted-foreground" />
                <span className="text-foreground">Applied: {formatDate(application?.appliedDate)}</span>
                <span className="text-muted-foreground">({getDaysAgo(application?.appliedDate)})</span>
              </div>
              {application?.lastUpdated && (
                <div className="flex items-center gap-2">
                  <Icon name="Clock" size={16} className="text-muted-foreground" />
                  <span className="text-foreground">Updated: {formatDate(application?.lastUpdated)}</span>
                </div>
              )}
            </div>

            {application?.interviewDate && (
              <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <Icon name="Video" size={16} className="text-indigo-600 dark:text-indigo-400" />
                  <span className="font-medium text-indigo-900 dark:text-indigo-100">
                    Interview: {formatDate(application?.interviewDate)} at {application?.interviewTime}
                  </span>
                </div>
                {application?.interviewType && (
                  <p className="text-xs text-indigo-700 dark:text-indigo-300 mt-1 ml-6">
                    {application?.interviewType}
                  </p>
                )}
              </div>
            )}

            {isExpanded && (
              <div className="mt-4 pt-4 border-t border-border">
                <h4 className="text-sm font-semibold text-foreground mb-3">Application Timeline</h4>
                <div className="space-y-3">
                  {application?.timeline?.map((event, index) => (
                    <div key={index} className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon name={event?.icon} size={16} className="text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{event?.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{event?.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">{formatDate(event?.date)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {application?.recruiterNotes && (
                  <div className="mt-4 p-3 bg-muted rounded-lg">
                    <p className="text-xs font-semibold text-foreground mb-1">Recruiter Notes:</p>
                    <p className="text-sm text-muted-foreground">{application?.recruiterNotes}</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                iconName="Eye"
                iconPosition="left"
                onClick={() => navigate(`/job-details?id=${application?.jobId}`)}
              >
                View Job
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
                iconPosition="left"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? 'Less Details' : 'More Details'}
              </Button>
              {application?.communicationCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="MessageSquare"
                  iconPosition="left"
                  onClick={() => onViewDetails(application)}
                >
                  Messages ({application?.communicationCount})
                </Button>
              )}
              {application?.status !== 'rejected' && application?.status !== 'offer-received' && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  iconPosition="left"
                  onClick={() => onWithdraw(application)}
                  className="text-error hover:text-error"
                >
                  Withdraw
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationCard;